import { SvgIcon, Tab, Tabs } from '@mui/material';
import {
  RiCoupon2Line,
  RiMoneyEuroCircleLine,
  RiShieldUserLine,
  RiShipLine,
} from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import CustomCalendar from '../../../components/CustomCalendar/CustomCalendar';
import { renderSkeletonCards } from '../../../components/SkeletonElements';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useCustomCalendarManager from '../../../hooks/useCustomCalendarManager';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import {
  AllowedCalendarRangeTypes,
  CrewMemberStatus,
  VesselStatus,
} from '../../../types/core';
import { ContractPeriods } from '../../../types/dashboard';
import { MONTHS } from '../../../utils/date';
import { DashboardCard } from './DashboardCards';
import DashboardPageWrapper from './DashboardPageWrapper';

const getSortedContractPeriods = (
  contractPeriods: ContractPeriods[],
  t: (text: string) => string,
): string[] =>
  [...contractPeriods]
    .sort((a, b) => a.contractName.localeCompare(b.contractName))
    .reduce((acc, { contractName, periods }) => {
      acc.push(
        ...[...periods]
          .sort((a, b) => a.year - b.year || a.month - b.month)
          .map(
            ({ year, month }) =>
              `${contractName} - ${t(`months.${MONTHS[month - 1]}`).slice(0, 3)} ${year.toString().slice(2)}`,
          ),
      );
      return acc;
    }, [] as string[]);

const OperatorDashboard = () => {
  const { t } = useLanguage();
  const { user } = useCurrentUser();
  const { getOperatorDashboardInfo } = useIotsApi();
  const [selectedTab, setSelectedTab] = useState(0);

  const {
    data: dashboardInfo = null,
    isFetching: isFetchingDashboardInfo,
    isLoading: isLoadingDashboardInfo,
    error: dashboardInfoError,
  } = useQuery({
    queryKey: ['dashboard', user.id],
    queryFn: () => getOperatorDashboardInfo(),
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  useQueryErrorNotifier([dashboardInfoError]);

  const { isLoading: isLoadingCalendarData, customCalendarProps } =
    useCustomCalendarManager(
      `dashboard-${user.id}`,
      AllowedCalendarRangeTypes.Daily,
    );

  const { pendingTasksCount, readyInvoicesList, awaitingInvoicesList } =
    useMemo<{
      pendingTasksCount: number;
      readyInvoicesList: string[];
      awaitingInvoicesList: string[];
    }>(() => {
      if (!dashboardInfo) {
        return {
          pendingTasksCount: 0,
          readyInvoicesList: [],
          awaitingInvoicesList: [],
        };
      }

      const { journey, skipper, vessel, invoice } = dashboardInfo;

      return {
        pendingTasksCount:
          journey.notSubmitted +
          vessel.uncompliant +
          skipper.uncompliant +
          invoice.incompleteMonths +
          invoice.readyInvoices,
        awaitingInvoicesList: getSortedContractPeriods(
          dashboardInfo.invoice.incompleteMonthsPeriods,
          t,
        ),
        readyInvoicesList: getSortedContractPeriods(
          dashboardInfo.invoice.readyInvoicesPeriods,
          t,
        ),
      };
    }, [dashboardInfo, t]);

  return (
    <DashboardPageWrapper
      title={`${t('content.welcome')}, ${user.firstName}`}
      subtitle={
        dashboardInfo
          ? `${t('content.you-have-pending-tasks', {
              PENDING_TASKS: pendingTasksCount.toString(),
            })}`
          : null
      }
      isLoading={isFetchingDashboardInfo || isLoadingCalendarData}
    >
      {isLoadingDashboardInfo ? (
        <div className="flex justify-center items-center min-h-[60vh]">
          {renderSkeletonCards(3)}
        </div>
      ) : !dashboardInfo ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <h1 className="text-4xl font-bold">
            {t('content.no-admin-dashboard-data')}
          </h1>
        </div>
      ) : (
        <>
          <Tabs
            className="mb-2 md:hidden"
            variant="fullWidth"
            onChange={(_event, value) => {
              setSelectedTab(value as number);
            }}
            value={selectedTab}
          >
            <Tab label={t('content.tasks')} />
            <Tab label={t('content.journeys')} />
          </Tabs>
          <div
            className={`${selectedTab === 0 ? 'grid' : 'hidden'} md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2`}
          >
            <DashboardCard
              icon={<SvgIcon component={RiShieldUserLine} />}
              title={t('content.skippers')}
              items={[
                {
                  label: t('content.uncompliant'),
                  amount: dashboardInfo.skipper.uncompliant,
                  warning: dashboardInfo.skipper.uncompliant > 0,
                },
                {
                  label: t('content.expiring-soon'),
                  amount: dashboardInfo.skipper.expiredSoon,
                  warning: false,
                },
              ].filter(({ amount }) => amount > 0)}
              actionUrl="/admin/crew"
              actionUrlFilters={
                dashboardInfo.skipper.uncompliant > 0
                  ? [
                      {
                        field: 'status',
                        operator: 'is',
                        value: CrewMemberStatus.Uncompliant,
                      },
                    ]
                  : dashboardInfo.skipper.expiredSoon > 0
                    ? [
                        {
                          field: 'expiredSoon',
                          operator: 'is',
                          value: true,
                        },
                      ]
                    : []
              }
            />
            <DashboardCard
              icon={<SvgIcon component={RiShipLine} />}
              title={t('content.vessels')}
              items={[
                {
                  label: t('content.uncompliant'),
                  amount: dashboardInfo.vessel.uncompliant,
                  warning: dashboardInfo.vessel.uncompliant > 0,
                },
                {
                  label: t('content.expiring-soon'),
                  amount: dashboardInfo.vessel.expiredSoon,
                  warning: false,
                },
              ].filter(({ amount }) => amount > 0)}
              actionUrl="/admin/vessels"
              actionUrlFilters={
                dashboardInfo.vessel.uncompliant > 0
                  ? [
                      {
                        field: 'status',
                        operator: 'is',
                        value: VesselStatus.Uncompliant,
                      },
                    ]
                  : dashboardInfo.vessel.expiredSoon > 0
                    ? [
                        {
                          field: 'expiredSoon',
                          operator: 'is',
                          value: true,
                        },
                      ]
                    : []
              }
            />
            <DashboardCard
              icon={<SvgIcon component={RiCoupon2Line} />}
              title={t('content.journeys')}
              lastUpdate={dayjs(dashboardInfo.journey.lastUpdateDate)}
              items={[
                {
                  label: t('content.not-submitted'),
                  amount: dashboardInfo.journey.notSubmitted,
                  warning: dashboardInfo.journey.notSubmitted > 0,
                },
              ].filter(({ amount }) => amount > 0)}
              actionUrl="/admin/journeys"
            />
            <DashboardCard
              icon={<SvgIcon component={RiMoneyEuroCircleLine} />}
              title={t('content.invoices')}
              lastUpdate={dayjs(dashboardInfo.invoice.lastCalculationUpdate)}
              items={[
                {
                  label: t('content.awaiting-generation'),
                  amount: dashboardInfo.invoice.incompleteMonths,
                  warning: dashboardInfo.invoice.incompleteMonths > 0,
                  helperContent: (
                    <ul>
                      {awaitingInvoicesList.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ),
                },
                {
                  label: t('content.ready-to-be-invoiced'),
                  amount: dashboardInfo.invoice.readyInvoices,
                  warning: dashboardInfo.invoice.readyInvoices > 0,
                  helperContent: (
                    <ul>
                      {readyInvoicesList.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ),
                },
              ].filter(({ amount }) => amount > 0)}
              actionUrl="/admin/invoices"
            />
          </div>
          <div className={`${selectedTab === 1 ? 'block' : 'hidden'} md:block`}>
            <CustomCalendar {...customCalendarProps} />
          </div>
        </>
      )}
    </DashboardPageWrapper>
  );
};

export default OperatorDashboard;
